import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { Files } from "lucide-react";
import { useGetConversationByIdQuery } from "@/redux/api/conversations_new.rtkq";
import { useEnrichedSourceDocuments } from "@/hooks/useEnrichedSourceDocuments";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Answer } from "@/components/types";
import ConversationMainArea from "@/components/conversation/main/ConversationMainArea";
import SourceDocumentsSidebar from "@/components/conversation/sourceDocuments/SourceDocumentsMainSidebar";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const ConversationPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [nudgedDocumentId, setNudgedDocumentId] = useState<string | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const { conversationId } = useParams();
  const isMobile = useIsMobile();

  const { data: conversationData, isLoading: isQueryLoading } = useGetConversationByIdQuery({
    conversationId: conversationId as string,
  });

  // Extract answer and source documents from chat history
  const answer = useMemo(() => {
    if (!conversationData?.chat_history) return null;

    // Find the assistant message with answer content
    const assistantMessage = conversationData.chat_history.find(
      (msg) => msg.role === "assistant" && typeof msg.content === "object"
    );

    return (assistantMessage?.content as Answer) || null;
  }, [conversationData]);

  const sourceDocuments = useMemo(() => {
    if (!conversationData?.chat_history) return [];

    // Find the assistant message with source documents
    const assistantMessage = conversationData.chat_history.find((msg) => msg.role === "assistant" && "source_documents" in msg);

    return assistantMessage?.source_documents || [];
  }, [conversationData]);

  // Enrich source documents with quotes from answer
  const enrichedSourceDocuments = useEnrichedSourceDocuments(answer, sourceDocuments);

  useEffect(() => {
    if (isQueryLoading !== undefined) {
      setIsLoading(isQueryLoading);
    }
  }, [isQueryLoading]);

  const handleDocumentClick = (documentId: string) => {
    if (isMobile) {
      setSourcesOpen(true);
    }

    // Find the document element after sheet may open
    const scrollToDoc = () => {
      const documentElement = document.querySelector(`[data-document-id="${documentId}"]`);
      if (documentElement) {
        documentElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        setNudgedDocumentId(documentId);
        setTimeout(() => {
          setNudgedDocumentId(null);
        }, 1000);
      }
    };

    if (isMobile) {
      setTimeout(scrollToDoc, 320);
    } else {
      scrollToDoc();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center">
        <div className="text-center space-y-6 p-8">
          <h2 className="text-2xl max-md:text-xl max-sm:text-lg font-semibold text-indigo-900">טוען שיחה...</h2>
        </div>
      </div>
    );
  }

  if (!conversationData) {
    return (
      <div className="flex h-[100dvh] items-center justify-center">
        <div className="text-center space-y-6 p-8">
          <h2 className="text-2xl max-md:text-xl max-sm:text-lg font-semibold text-indigo-900">לא נמצאו שיחות</h2>
          <p className="text-base max-sm:text-sm text-slate-600">אין שיחות שמורות במכשיר זה</p>
        </div>
      </div>
    );
  }

  const sourcesSidebar = (
    <SourceDocumentsSidebar
      enrichedSourceDocuments={enrichedSourceDocuments}
      onDocumentNudge={setNudgedDocumentId}
      nudgedDocumentId={nudgedDocumentId}
      className={isMobile ? "w-full" : undefined}
    />
  );

  return (
    <div className="relative flex h-[calc(100dvh-3.5rem)] min-h-0 md:h-[100dvh]" dir="rtl">
      <ConversationMainArea conversationData={conversationData} onDocumentClick={handleDocumentClick} />

      {isMobile ? (
        <>
          <Button
            type="button"
            size="sm"
            className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] end-4 z-40 gap-2 shadow-lg"
            onClick={() => setSourcesOpen(true)}
          >
            <Files className="size-4" aria-hidden />
            מקורות
          </Button>
          <Sheet open={sourcesOpen} onOpenChange={setSourcesOpen}>
            <SheetContent
              side="bottom"
              className="flex h-[min(85dvh,40rem)] flex-col gap-0 p-0 pb-[env(safe-area-inset-bottom)]"
            >
              <SheetHeader className="border-b border-border px-4 py-3 text-start">
                <SheetTitle>מקורות</SheetTitle>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-hidden">{sourcesSidebar}</div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        sourcesSidebar
      )}
    </div>
  );
};

export default ConversationPage;
